import type { MarkerRefToCommentIdMap, UnreadInlineComment } from '../types/unreadCommentsTypes';

export enum PageMode {
	EDITOR,
	LIVE,
	RENDERER,
}

export type UnreadPubSubParams = {
	isUnreadCommentsEnabled: boolean;
	inlineCommentRequestBody: {
		commentId?: string;
		markerRef?: string;
	};
	readCommentsListState: Set<string>;
	isNotCurrentUser: boolean;
	updateUnreadCommentsListState: (
		transform: (
			prevUnreadCommentsListState: UnreadInlineComment[],
			markerRefToCommentIdMap: MarkerRefToCommentIdMap,
		) => UnreadInlineComment[],
	) => void;
	markerRefToCommentIdMap: MarkerRefToCommentIdMap;
	isTopLevelComment: boolean;
	pageMode?: PageMode;
};

// add to the unread list state for new, reattached, unresolved inline comment, only if it's unread
// this applies to only collaborators because for the current user, the comment box will be open on unresolve
export const handleAddedInlineCommentForUnread = ({
	isUnreadCommentsEnabled,
	inlineCommentRequestBody,
	readCommentsListState,
	isNotCurrentUser,
	updateUnreadCommentsListState,
	isTopLevelComment,
	pageMode,
}: UnreadPubSubParams): void => {
	const { commentId, markerRef } = inlineCommentRequestBody;

	if (isUnreadCommentsEnabled && isNotCurrentUser && commentId && markerRef) {
		// this could be top level comment or reply
		const unreadCommentsToAdd: UnreadInlineComment[] = !readCommentsListState.has(commentId)
			? [
					{
						id: commentId,
						inlineMarkerRef: markerRef,
					},
				]
			: [];

		//if its a reply and the comment thread is open, dont increment the count
		const isMarkerRefOpen =
			pageMode === PageMode.EDITOR || pageMode === PageMode.LIVE
				? document.querySelector(`[id="${markerRef}"] .ak-editor-annotation-focus.true`)
				: document.querySelector(`[id="${markerRef}"][data-has-focus="true"]`);
		if (!isTopLevelComment && isMarkerRefOpen) {
			return;
		}

		updateUnreadCommentsListState((prevUnreadCommentsListState, markerRefToCommentIdMap) => {
			// for reattach / unresolve: if the comment is a top level comment, then add the replies, if not, don't need to add anything else
			if (isTopLevelComment && markerRefToCommentIdMap[markerRef]) {
				for (const reply of markerRefToCommentIdMap[markerRef]) {
					if (!readCommentsListState.has(reply)) {
						unreadCommentsToAdd.push({
							id: reply,
							inlineMarkerRef: markerRef,
						});
					}
				}
			}

			return [...prevUnreadCommentsListState, ...unreadCommentsToAdd];
		});
	}
};

// remove from the unread list state for resolved or deleted inline comment if it's unread
// the comment is already considered read for yourself when you resolve, so we only need to check for collaborators
export const handleObliteratedInlineCommentForUnread = ({
	isUnreadCommentsEnabled,
	inlineCommentRequestBody,
	readCommentsListState,
	isNotCurrentUser,
	updateUnreadCommentsListState,
	isTopLevelComment,
}: UnreadPubSubParams): void => {
	const { commentId, markerRef } = inlineCommentRequestBody;
	if (
		isUnreadCommentsEnabled &&
		isNotCurrentUser &&
		commentId &&
		markerRef &&
		!readCommentsListState.has(commentId)
	) {
		const unreadCommentsToObliterate: Set<string> = new Set([commentId]);

		updateUnreadCommentsListState((prevUnreadCommentsListState, markerRefToCommentIdMap) => {
			// for resolve / delete: if the comment is a top level comment, then remove the replies as well
			if (isTopLevelComment && markerRefToCommentIdMap[markerRef]) {
				for (const reply of markerRefToCommentIdMap[markerRef]) {
					unreadCommentsToObliterate.add(reply);
				}
			}

			return prevUnreadCommentsListState.filter(
				(comment) => !unreadCommentsToObliterate.has(comment.id),
			);
		});
	}
};
